import { Header } from "@/app/layout/Header";
import { Main } from "@/app/layout/Main";
import { Button } from "@devhop/ui";
import { useNavigate, useSearch } from "@tanstack/react-router";
import { useOvertimeRequestsQuery } from "../api/queries";
import { OvertimeRequestTable } from "../ui/OvertimeRequestTable";
import { HelpCircle, PlusIcon } from "lucide-react";
import { HrTour } from "../tour/HrTour";
import { useHrTour } from "../tour/useHrTour";
import { overtimeRequestListTourSteps } from "../tour/hrTourSteps";

type OvertimeSortBy = "workDate" | "startTime" | "hours" | "status";

export function OvertimeRequestListPage() {
  const navigate = useNavigate({ from: "/app/hr/overtime/requests" });
  const search = useSearch({ from: "/app/hr/overtime/requests" });
  const statusFilter = search.status;
  const offset = search.offset;
  const limit = search.limit;
  const sortBy = search.sortBy as OvertimeSortBy | undefined;
  const sortOrder = search.sortOrder as "asc" | "desc" | undefined;

  const query = useOvertimeRequestsQuery({
    limit,
    offset,
    status: statusFilter as "pending" | "approved" | "rejected" | undefined,
    sortBy,
    sortOrder,
  });
  const tour = useHrTour("hr-ot-list-tour-completed");

  const data = query.data?.data ?? [];
  const totalCount = query.data?.meta?.total ?? 0;

  return (
    <>
      <Header />
      <Main>
        <div className="mb-6 flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <div>
            <div className="flex items-center gap-2">
            <h1 className="font-bold text-2xl tracking-tight">ຄຳຂໍ OT</h1>
            <Button
              type="button"
              variant="ghost"
              size="icon"
              className="size-8"
              onClick={tour.startTour}
              aria-label="ເລີ່ມທົດສອບການນຳທາງ"
              title="ເລີ່ມທົດສອບການນຳທາງ"
            >
              <HelpCircle className="size-4" />
            </Button>
            </div>
            <p className="mt-1 text-muted-foreground">
              ລາຍງານການເຮັດວຽກລ່ວງເວລາ ແລະ ຕິດຕາມສະຖານະ
            </p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              onClick={() => navigate({ to: "/app/hr/overtime/requests/new" })}
              className="bg-black text-white hover:bg-black/90"
            >
              <PlusIcon className="size-4" />
              ສ້າງຄຳຂໍ OT ໃໝ່
            </Button>
          </div>
        </div>

        <div data-tourid="hr-ot-list-table">
          <OvertimeRequestTable
            data={data}
            isLoading={query.isLoading}
            totalCount={totalCount}
            offset={offset}
            limit={limit}
            onPageChange={(o, l) =>
              navigate({ search: { ...search, offset: o, limit: l } })
            }
            statusFilter={statusFilter}
            onStatusFilterChange={(s) =>
              navigate({ search: { ...search, status: s, offset: 0 } })
            }
            sortBy={sortBy}
            sortOrder={sortOrder}
            onSortingChange={(id, desc) =>
              navigate({
                search: {
                  ...search,
                  sortBy: id,
                  sortOrder: desc ? "desc" : "asc",
                  offset: 0,
                },
              })
            }
          />
        </div>

        <HrTour
          steps={overtimeRequestListTourSteps}
          run={tour.run}
          onCallback={tour.handleJoyrideCallback}
        />
      </Main>
    </>
  );
}
